import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "filter-box" }
const _hoisted_2 = { class: "button-row" }

import {
  DashboardDropdownFilterBuilding,
  DashboardDropdownFilterComplex,
  DashboardDropdownFilterRealEstate
} from '../AnalyticsDashboard/components/filters'
import {
  ComplexProperties,
  BuildingProperties,
  ComplexFeature,
  RealEstateProperties,
  RealEstateFeature
} from '../types'

import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import { ref, watch, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { InformationResponse } from '@/api-client/models/portfolio/Contracts'
import { useCurrentFeatureFilters } from '@/composables/useFilters'
type Props = {
  information: InformationResponse | undefined
  isCollapsed?: boolean
}
type Emits = {
  (e: 'resetForm'): void
  (e: 'submitForm'): void
}

export default /*@__PURE__*/_defineComponent({
  __name: 'PortfolioAnalysisFilters',
  props: {
    information: {},
    isCollapsed: { type: Boolean }
  },
  emits: ["resetForm", "submitForm"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emits = __emit
const vuex = useStore()

const filters = useCurrentFeatureFilters()
const { t } = useI18n()

const selectedBuildings = ref<BuildingProperties[]>([])

const htmlRef = ref<HTMLElement | null>(null)
const allComplexes = computed<ComplexProperties[]>(() =>
  vuex.getters.allComplexes.map((x: ComplexFeature) => x.properties)
)
const allRealEstates = computed<RealEstateProperties[]>(() =>
  vuex.getters.allRealEstates.map((x: RealEstateFeature) => x.properties)
)
const allBuildings = computed<BuildingProperties[]>(() =>
  vuex.getters.allBuildings.map((x: ComplexFeature) => x.properties)
)

const filteredRealEstates = computed<RealEstateProperties[]>(() => {
  if (!filters.value?.current.complex) {
    return allRealEstates.value
  }
  return allRealEstates.value.filter(
    (x) => x.complexId === filters.value?.current.complex?.belongsToId
  )
})

const filteredBuildings = computed<BuildingProperties[]>(() => {
  if (!filters.value?.current) return []
  if (
    !filters.value.current.complex ||
    !('realEstate' in filters.value.current && filters.value.current.realEstate)
  ) {
    return allBuildings.value
  }
  const realEstate = filters.value.current.realEstate
  const complexId = realEstate?.complexId ?? filters.value.current.complex?.belongsToId
  return allBuildings.value.filter((x) =>
    !x.complexId || x.complexId === 'undefined'
      ? x.realEstateId === realEstate?.id
      : x.complexId === complexId
  )
})

watch(
  () => props.isCollapsed,
  async () => {
    await nextTick()
    htmlRef.value?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    })
  }
)

watch(
  () => filters.value?.current.buildings,
  (value) => {
    if (
      value &&
      filters.value &&
      !('realEstate' in filters.value.current && filters.value.current.realEstate) &&
      value.length > 0
    ) {
      updateComplexSelection(value)
    }
  }
)

function resetForm() {
  emits('resetForm')
}

function complexSelected(complex: ComplexProperties | undefined) {
  filters.value?.$patch({
    current: {
      complex: complex,
      realEstate: undefined,
      buildings: []
    }
  })
}

function realEstateSelected(realEstate: RealEstateProperties | undefined) {
  const complex = allComplexes.value.find((x) => x.belongsToId === realEstate?.complexId)
  selectedBuildings.value = []
  filters.value?.$patch({ current: { complex: complex, realEstate: realEstate, buildings: [] } })
}

function buildingsSelected(buildings: BuildingProperties[]) {
  selectedBuildings.value = buildings
}

function buildingFilterClosed() {
  filters.value?.$patch({ current: { buildings: selectedBuildings.value } })
}

function updateComplexSelection(buildings: BuildingProperties[]) {
  const realEstate = allRealEstates.value.find((x) => x.id === buildings[0].realEstateId)
  const complex = allComplexes.value.find((x) => x.belongsToId === realEstate?.complexId)
  selectedBuildings.value = []
  filters.value?.$patch({ current: { complex: complex, realEstate: realEstate } })
}

return (_ctx: any,_cache: any) => {
  return (!_ctx.isCollapsed)
    ? (_openBlock(), _createBlock(_Transition, {
        key: 0,
        name: "slide"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", {
              ref_key: "htmlRef",
              ref: htmlRef,
              class: "filter-container"
            }, [
              _createVNode(_unref(DashboardDropdownFilterComplex), {
                complexes: allComplexes.value,
                selectedComplex: _unref(filters)?.current.complex,
                optionsWithData: new Set(_ctx.information?.optionsWithData.complexes ?? []),
                onSelectionChanged: complexSelected
              }, null, 8, ["complexes", "selectedComplex", "optionsWithData"]),
              (_unref(filters) && 'realEstate' in _unref(filters)?.current)
                ? (_openBlock(), _createBlock(_unref(DashboardDropdownFilterRealEstate), {
                    key: 0,
                    realEstates: filteredRealEstates.value,
                    selectedRealEstate: _unref(filters).current.realEstate,
                    presentAsMultiSelect: !!_unref(filters).current.complex,
                    optionsWithData: new Set(_ctx.information?.optionsWithData.realEstates ?? []),
                    onSelectionChanged: realEstateSelected
                  }, null, 8, ["realEstates", "selectedRealEstate", "presentAsMultiSelect", "optionsWithData"]))
                : _createCommentVNode("", true),
              _createVNode(_unref(DashboardDropdownFilterBuilding), {
                buildings: filteredBuildings.value,
                selectedBuildings: selectedBuildings.value,
                presentAsMultiSelect: 
              _unref(filters)?.current && 'realEstate' in _unref(filters).current && !!_unref(filters).current.realEstate
            ,
                optionsWithData: new Set(_ctx.information?.optionsWithData.constructions ?? []),
                onSelectionChanged: buildingsSelected,
                onClose: buildingFilterClosed
              }, null, 8, ["buildings", "selectedBuildings", "presentAsMultiSelect", "optionsWithData"])
            ], 512),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(ButtonOutline, {
                size: "small",
                buttonText: _unref(t)('general__text__dashboard_reset_selections'),
                onOnClicked: resetForm
              }, null, 8, ["buttonText"]),
              _createVNode(ButtonPrimary, {
                size: "small",
                disabled: _unref(filters)?.isCurrentFilterSubmitted,
                buttonText: _unref(t)('general__text__dashboard_update_selections'),
                onOnClicked: _cache[0] || (_cache[0] = ($event: any) => (_ctx.$emit('submitForm')))
              }, null, 8, ["disabled", "buttonText"])
            ])
          ])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})